// EmployeesImport.js - Import employees from CSV with guide
import React, { useEffect, useState, useRef } from 'react';
import { API_BASE } from '../config';
import { useTranslation } from '../i18n/LanguageContext';
import ModernPageLayout, { ModernCard } from './ModernPageLayout';
import { Home, Upload, FileText, CheckCircle, XCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import LanguageSelector from './LanguageSelector';

// Guide fields: same as new employee form. Required = first_name, last_name only; rest optional.
const CSV_GUIDE_FIELDS = [
  { key: 'first_name', nameEn: 'first_name', nameFr: 'prenom', descEn: 'First name (required)', descFr: 'Prénom (obligatoire)' },
  { key: 'last_name', nameEn: 'last_name', nameFr: 'nom', descEn: 'Last name (required)', descFr: 'Nom (obligatoire)' },
  { key: 'email', nameEn: 'email', nameFr: 'email', descEn: 'Email (optional). For login; must be unique if provided.', descFr: 'Email (optionnel). Pour connexion; doit être unique si fourni.' },
  { key: 'phone', nameEn: 'phone', nameFr: 'telephone', descEn: 'Phone (optional)', descFr: 'Téléphone (optionnel)' },
  { key: 'department', nameEn: 'department', nameFr: 'departement', descEn: 'Department (optional, e.g. HR, IT)', descFr: 'Département (optionnel, ex: RH, Informatique)' },
  { key: 'position', nameEn: 'position', nameFr: 'poste', descEn: 'Job title (optional)', descFr: 'Poste (optionnel)' },
  { key: 'hire_date', nameEn: 'hire_date', nameFr: 'date_embauche', descEn: 'Hire date (optional). MM/DD/YYYY', descFr: 'Date embauche (optionnel). MM/JJ/AAAA' },
  { key: 'employment_type', nameEn: 'employment_type', nameFr: 'type_contrat', descEn: 'Employment type (optional). CDI, CDD, etc.', descFr: 'Type de contrat (optionnel). CDI, CDD, etc.' },
  { key: 'matricule', nameEn: 'matricule', nameFr: 'matricule', descEn: 'Matricule (optional, unique)', descFr: 'Matricule (optionnel, unique)' },
  { key: 'employee_code', nameEn: 'employee_code', nameFr: 'code_employe', descEn: 'Employee code (optional). Auto-generated if empty.', descFr: 'Code employé (optionnel). Auto si vide.' },
  { key: 'employee_password', nameEn: 'employee_password', nameFr: 'employee_password', descEn: 'Login password (optional). Default: admin123', descFr: 'Mot de passe connexion (optionnel). Défaut: admin123' },
  { key: 'employee_role', nameEn: 'employee_role', nameFr: 'employee_role', descEn: 'User role (optional). employee | manager | admin', descFr: 'Rôle (optionnel). employee | manager | admin' },
  { key: 'salary_type', nameEn: 'salary_type', nameFr: 'type_salaire', descEn: 'Salary type (optional). hourly | monthly | annual', descFr: 'Type salaire (optionnel). hourly | monthly | annual' },
  { key: 'salary_currency', nameEn: 'salary_currency', nameFr: 'devise_salaire', descEn: 'Currency (optional). EUR | USD | XOF', descFr: 'Devise (optionnel). EUR | USD | XOF' },
  { key: 'hourly_rate', nameEn: 'hourly_rate', nameFr: 'taux_horaire', descEn: 'Hourly rate (optional, number)', descFr: 'Taux horaire (optionnel, nombre)' },
  { key: 'salary_monthly', nameEn: 'salary_monthly', nameFr: 'salaire_mensuel', descEn: 'Monthly salary (optional, number)', descFr: 'Salaire mensuel (optionnel, nombre)' },
  { key: 'administration', nameEn: 'administration', nameFr: 'administration', descEn: 'Administration (optional)', descFr: 'Administration (optionnel)' },
  { key: 'service', nameEn: 'service', nameFr: 'service', descEn: 'Service (optional)', descFr: 'Service (optionnel)' },
  { key: 'bureau', nameEn: 'bureau', nameFr: 'bureau', descEn: 'Bureau (optional)', descFr: 'Bureau (optionnel)' },
  { key: 'grade', nameEn: 'grade', nameFr: 'grade', descEn: 'Grade (optional)', descFr: 'Grade (optionnel)' },
  { key: 'echelle', nameEn: 'echelle', nameFr: 'echelle', descEn: 'Echelle (optional)', descFr: 'Échelle (optionnel)' },
  { key: 'indice', nameEn: 'indice', nameFr: 'indice', descEn: 'Indice (optional, number)', descFr: 'Indice (optionnel, nombre)' },
  { key: 'statut', nameEn: 'statut', nameFr: 'statut', descEn: 'Statut (optional)', descFr: 'Statut (optionnel)' },
  { key: 'date_titularisation', nameEn: 'date_titularisation', nameFr: 'date_titularisation', descEn: 'Date titularisation (optional). MM/DD/YYYY', descFr: 'Date titularisation (optionnel). MM/JJ/AAAA' },
  { key: 'notes', nameEn: 'notes', nameFr: 'notes', descEn: 'Notes (optional)', descFr: 'Notes (optionnel)' }
];
const REQUIRED_KEYS = ['first_name', 'last_name'];

// Format today as MM/DD/YYYY for guide example (always current, easy to read)
const getExampleDateStr = () => {
  const d = new Date();
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
};

const EmployeesImport = ({ user, onNavigate }) => {
  const { t, language } = useTranslation();
  const lang = language === 'fr' ? 'fr' : 'en';
  const exampleDateStr = getExampleDateStr();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') return window.innerWidth <= 768;
    return false;
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const onChange = () => setIsMobile(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setResult(null);
    setError('');
  };

  const handleUpload = async () => {
    if (!file) {
      setError(t('employees.import_select_file') || 'Select a CSV file');
      return;
    }
    setUploading(true);
    setError('');
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch(`${API_BASE}/api/employees/import-csv?lang=${lang}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const msg = data.code === 'CSV_LANGUAGE_MISMATCH' || data.code === 'CSV_LANGUAGE_UNKNOWN'
          ? (t('employees.import_language_mismatch') || data.error)
          : (data.error || `HTTP ${response.status}`);
        setError(msg);
        setResult(null);
        return;
      }
      setResult(data);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setError(err.message || t('employees.import_error') || 'Import failed');
      setResult(null);
    } finally {
      setUploading(false);
    }
  };

  const handleBack = () => {
    if (onNavigate) onNavigate('/employees');
    else window.history.back();
  };

  return (
    <ModernPageLayout
      title={t('employees.import_title') || 'Import employees (CSV)'}
      subtitle={t('employees.import_subtitle') || 'Upload a CSV file to create employees in bulk'}
      icon={<FileText size={24} />}
      headerActions={!isMobile ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <LanguageSelector />
          <button
            onClick={handleBack}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '10px 16px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.3)',
              background: 'rgba(255,255,255,0.1)', color: '#fff', cursor: 'pointer', fontSize: '14px'
            }}
          >
            <Home size={18} />
          </button>
        </div>
      ) : null}
    >
      {isMobile && (
        <div style={{ marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <LanguageSelector />
          <button
            onClick={handleBack}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '8px',
              padding: '10px 14px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.3)',
              background: 'rgba(255,255,255,0.1)', color: '#fff', cursor: 'pointer', fontSize: '14px'
            }}
          >
            <Home size={18} /> {t('employees.back_to_list') || 'Back to list'}
          </button>
        </div>
      )}
      {/* Back to employees - visible on page */}
      <div style={{ marginBottom: '20px' }}>
        <button
          onClick={handleBack}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            padding: '10px 18px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.25)',
            background: 'rgba(255,255,255,0.08)', color: '#fff', cursor: 'pointer', fontSize: '14px', fontWeight: 500
          }}
        >
          <ArrowLeft size={18} /> {t('employees.back_to_employees') || 'Back to employees'}
        </button>
      </div>
      <ModernCard title={t('employees.import_guide_title') || 'CSV format guide'} icon={<AlertCircle size={20} />} style={{ marginBottom: '24px' }}>
        <p style={{ color: 'rgba(255,255,255,0.85)', marginBottom: '16px', fontSize: '14px' }}>
          {t('employees.import_guide_intro') || 'Your CSV must have a header row. Use comma (,) as separator. For text containing commas, wrap the value in double quotes ("). First row = column names (see below).'}
        </p>
        <p style={{ color: 'rgba(255,255,255,0.75)', marginBottom: '12px', fontSize: '13px' }}>
          {t('employees.import_guide_form_match') || (lang === 'fr' ? "Les colonnes ci-dessous correspondent au formulaire Nouvel employé. Seuls first_name et last_name sont obligatoires ; les autres sont optionnelles. Inclure employee_password et employee_role pour le compte de connexion." : "The columns below match the New Employee form. Only first_name and last_name are required; all others are optional. Include employee_password and employee_role for the login account.")}
        </p>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                <th style={{ textAlign: 'left', padding: '8px', color: 'rgba(255,255,255,0.9)' }}>Column name (CSV header)</th>
                <th style={{ textAlign: 'left', padding: '8px', color: 'rgba(255,255,255,0.9)' }}>Description / Acceptable format</th>
              </tr>
            </thead>
            <tbody>
              {CSV_GUIDE_FIELDS.map((f) => {
                const name = lang === 'fr' ? f.nameFr : f.nameEn;
                let desc = lang === 'fr' ? f.descFr : f.descEn;
                if (f.key === 'hire_date' || f.key === 'date_titularisation') {
                  desc = lang === 'fr' ? `${f.descFr} (ex: ${exampleDateStr})` : `${f.descEn} (e.g. ${exampleDateStr})`;
                }
                const required = REQUIRED_KEYS.includes(f.key);
                return (
                  <tr key={f.key} style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                    <td style={{ padding: '8px', color: required ? '#fbbf24' : '#fff' }}><code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 6px', borderRadius: '4px' }}>{name}</code> {required && '*'}</td>
                    <td style={{ padding: '8px', color: 'rgba(255,255,255,0.8)' }}>{desc}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p style={{ color: 'rgba(255,255,255,0.7)', marginTop: '16px', fontSize: '13px' }}>
          {lang === 'fr'
            ? (t('employees.import_guide_footer_fr', { exampleDate: exampleDateStr }) || `Exemple d'en-tête : prenom,nom,email,departement,poste. Dates : MM/JJ/AAAA (ex: ${exampleDateStr}). Colonnes optionnelles vides acceptées. code_employe et matricule doivent être uniques. Chaque employé reçoit un compte de connexion (email + employee_password, employee_role).`)
            : (t('employees.import_guide_footer', { exampleDate: exampleDateStr }) || `Example header: first_name,last_name,email,department,position. Dates: MM/DD/YYYY (e.g. ${exampleDateStr}). Leave optional columns empty. employee_code and matricule must be unique. Each employee gets a login account (email + employee_password, employee_role).`)}
        </p>
      </ModernCard>

      <ModernCard title={t('employees.import_upload_title') || 'Upload CSV or Excel'} icon={<Upload size={20} />} style={{ marginBottom: '24px' }}>
        <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', marginBottom: '10px' }}>
          {t('employees.import_accept_types') || 'CSV or Excel (.xlsx, .xls) accepted.'}
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls,text/csv,application/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          onChange={handleFileChange}
          style={{ marginBottom: '12px', color: 'rgba(255,255,255,0.9)' }}
        />
        <button
          disabled={!file || uploading}
          onClick={handleUpload}
          style={{
            padding: '12px 24px', borderRadius: '12px', border: 'none',
            background: (file && !uploading) ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : 'rgba(255,255,255,0.2)',
            color: '#fff', fontWeight: 600, cursor: (file && !uploading) ? 'pointer' : 'not-allowed', fontSize: '14px'
          }}
        >
          {uploading ? (t('employees.importing') || 'Importing...') : (t('employees.import_upload_btn') || 'Import')}
        </button>
      </ModernCard>

      {error && (
        <ModernCard style={{ marginBottom: '24px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#ef4444' }}>
            <XCircle size={22} /> <span>{error}</span>
          </div>
        </ModernCard>
      )}

      {result && (
        <ModernCard style={{ marginBottom: '24px', background: result.failed > 0 ? 'rgba(251,191,36,0.08)' : 'rgba(16,185,129,0.1)', border: result.failed > 0 ? '1px solid rgba(251,191,36,0.35)' : '1px solid rgba(16,185,129,0.3)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: result.failed > 0 ? '#fbbf24' : '#10b981', marginBottom: '12px' }}>
            {result.failed > 0 ? <AlertCircle size={22} /> : <CheckCircle size={22} />}
            <strong>{result.failed > 0 ? (t('employees.import_partial') || 'Import completed with errors') : (t('employees.import_done') || 'Import completed')}</strong>
          </div>
          <p style={{ color: 'rgba(255,255,255,0.9)' }}>
            {result.created} {t('employees.import_created') || 'created'}.
            {result.failed > 0 && ` ${result.failed} ${t('employees.import_failed') || 'failed'}. ${t('employees.import_fix_and_retry') || 'Fix the rows below and re-upload your file.'}`}
          </p>
          {result.details?.failed?.length > 0 && (
            <div style={{ marginTop: '16px', padding: '12px', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
              <strong style={{ color: 'rgba(255,255,255,0.95)', fontSize: '13px' }}>{t('employees.import_errors_by_row') || 'Errors by row (fix these in your sheet and re-upload):'}</strong>
              <ul style={{ marginTop: '8px', paddingLeft: '20px', color: 'rgba(255,255,255,0.9)', fontSize: '13px', listStyle: 'disc' }}>
                {result.details.failed.map((f, i) => (
                  <li key={i} style={{ marginBottom: '4px' }}>
                    <strong>Row {f.row}</strong>: {f.reason}
                    {f.field && <span style={{ color: 'rgba(251,191,36,0.95)' }}> ({t('employees.import_field') || 'Field'}: {f.field})</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </ModernCard>
      )}
    </ModernPageLayout>
  );
};

export default EmployeesImport;
